import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { http } from '@/lib/api/client'

export type DemographicsRange = '30d' | '90d' | '12m' | 'all'

export type Market = 'tier1' | 'hubs' | 'other'

export type Channel = 'google' | 'meta' | 'referral' | 'tagged' | 'untagged'

/** The Audience filters: market, country, where people came from, the plan they're on now, how they signed up. */
export type AudienceFilters = Partial<
  Record<'market' | 'country' | 'channel' | 'plan' | 'method', string | null>
>

export interface CountRow {
  value: string
  label: string
  count: number
}

/** Who uses Arva SEO, for the Audience page: counts and shares only, nobody named; internal accounts left out. */
export interface Demographics {
  range: DemographicsRange
  from: string | null
  /** The filters applied (an empty list without any). */
  filters: Record<string, string>
  totals: {
    users: number
    open_users: number
    new_users: number
    workspaces: number
    active_1d: number
    active_7d: number
    active_30d: number
    /** Share of new workspaces that verified a website and finished an audit within a week; null without any a week old. */
    activation: number | null
    work_email: number | null
    marketing: number | null
  }
  /** The same figures for the period of equal length before the range; null for all time. */
  previous: {
    open_users: number
    new_users: number
    activation: number | null
    work_email: number | null
  } | null
  bucket: 'week' | 'month'
  signups: { start: string; end: string; users: number; open: number; activation: number | null }[]
  methods: { google: number; email: number; referred: number; work_email: number; personal_email: number }
  /** Where sign-ups came from: by channel and campaign, from the link's campaign tags (since 2026-10-16). */
  sources: { channels: CountRow[]; campaigns: CountRow[]; untagged: number }
  /** Sign-ups in the period by country, with how many of them became customers. */
  countries: { rows: (CountRow & { paid: number; paid_share: number })[]; unknown: number }
  /** Every country anyone signed up from (all time, unfiltered), for the Country filter. */
  country_options: (CountRow & { paid: number })[]
  target_markets: CountRow[]
  website_types: CountRow[]
  platforms: CountRow[]
  websites_per_workspace: { label: string; count: number }[]
  funnel: { key: string; label: string; count: number }[]
  /** What people did with the app's coach in the range (people, not clicks). */
  coach?: {
    tours: { tour: string; started: number; finished: number; closed: number }[]
    actions: {
      explain_opened: number
      prompt_copied: number
      stuck_opened: number
      coach_hidden: number
      view_expert: number
    }
  }
  by_channel: ({ start: string } & Record<Channel, number>)[]
  markets: { key: Market; count: number; paid_share: number }[]
  campaigns: {
    campaign: string
    channel: Channel
    source: string | null
    signups: number
    activated: number
    paid: number
  }[]
  /** Sign-ups by day (Monday first) and hour, in each person's own time zone. */
  heatmap: number[][]
  devices: {
    known: number
    device: { label: string; count: number }[]
    browser: { label: string; count: number }[]
    os: { label: string; count: number }[]
  }
  cohorts: { start: string; size: number; weeks: number[] }[]
  adoption: { key: string; label: string; share: number }[]
  plans: { key: string; name: string }[]
}

export function useDemographics(range: DemographicsRange, filters: AudienceFilters = {}) {
  const params: Record<string, string> = { range }
  for (const [key, value] of Object.entries(filters)) if (value) params[key] = value
  return useQuery({
    queryKey: ['demographics', params],
    queryFn: async () => (await http.get<{ data: Demographics }>('/demographics', params)).data,
    placeholderData: keepPreviousData,
  })
}
